import React from 'react';
import { IconButton, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

/**
 * SidebarToggleButton Component
 *
 * A floating action button that toggles the sidebar drawer visibility.
 * Features a notification badge when there are new events in cases.
 *
 * Features:
 * - Fixed position button for easy access
 * - Notification badge for new events
 * - Pulsing animation when new events are present
 * - Hover effects and smooth transitions
 * - Consistent styling with the main theme
 * - Smooth hide/show when drawer state changes
 */
const SidebarToggleButton = ({
  onClick,
  isOpen = false,
  newEventsCount = 0,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0'
}) => {
  const [shouldPlayIntro, setShouldPlayIntro] = React.useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    if (!isOpen) {
      setShouldPlayIntro(true);
    } else {
      setShouldPlayIntro(false);
    }
  }, [isOpen]);

  const introAnimation = shouldPlayIntro ? 'buttonFadeIn 0.35s ease-out' : null;
  const pulseAnimation = newEventsCount > 0 ? 'sidebarPulse 2s ease-in-out infinite' : null;
  const combinedAnimation = [introAnimation, pulseAnimation].filter(Boolean).join(', ') || 'none';

  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: 'fixed',
        top: isMobile ? 'auto' : '50%',
        bottom: isMobile ? '16px' : 'auto',
        left: isMobile ? '16px' : '10px',
        transform: isOpen
          ? (isMobile ? 'scale(0.9)' : 'translate(-60px, -50%) scale(0.9)')
          : (isMobile ? 'none' : 'translate(0, -50%) scale(1)'),
        zIndex: 1000,
        backgroundColor: primaryColor,
        color: '#fff',
        boxShadow: '0 10px 30px rgba(20, 24, 74, 0.22)',
        width: isMobile ? '48px' : '44px',
        height: isMobile ? '48px' : '44px',
        borderRadius: isMobile ? '18px' : '30px',
        backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,255,255,0.18)',
        opacity: isOpen ? 0 : 1,
        visibility: isOpen ? 'hidden' : 'visible',
        pointerEvents: isOpen ? 'none' : 'auto',
        transition: 'opacity 0.25s ease, transform 0.3s ease, box-shadow 0.25s ease, background-color 0.2s ease',
        animation: combinedAnimation,
        '@keyframes buttonFadeIn': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-50%) translateX(-10px) scale(0.9)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(-50%) translateX(0) scale(1)',
          }
        },
        '@keyframes sidebarPulse': {
          '0%, 100%': { boxShadow: '0 10px 30px rgba(20, 24, 74, 0.22)' },
          '50%': { boxShadow: `0 10px 30px rgba(20, 24, 74, 0.22), 0 0 0 8px rgba(87, 87, 176, 0.3)` },
        },
        '&:hover': {
          backgroundColor: hoverColor,
          transform: isMobile ? 'scale(1.04)' : 'translateY(-50%) scale(1.04)',
          boxShadow: '0 16px 34px rgba(20, 24, 74, 0.28)',
        },
      }}
    >
      <Badge
        badgeContent={newEventsCount}
        color="error"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.8rem',
            height: '20px',
            minWidth: '20px',
            fontWeight: 'bold',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <MenuIcon />
      </Badge>
    </IconButton>
  );
};

export default SidebarToggleButton;
